var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, } from "@/components/ui/card";
import { Bath, BedDouble, Ruler } from "lucide-react";
import { decodeHTMLEntity } from "@/lib/utils";
import FloorplanImageDialog from "./FloorplanImageDialog";
import FloorplanVirtualTourDialog from "./FloorplanVirtualTourDialog";
import FloorplanPropertyVideoDialog from "./FloorplanPropertyVideoDialog";
import FloorplanFeesDialog from "./FloorplanFeesDialog";
import { canShowRentCafeFeesEmbed } from "@/lib/rentcafeCostEstimator";
export default function PropertyCard(_a) {
    var _b;
    var floorplan = _a.floorplan, costEstimatorPropertyId = _a.costEstimatorPropertyId;
    var price = floorplan.minimumRent.toString();
    var detailUrl = ((_b = floorplan.permalink) === null || _b === void 0 ? void 0 : _b.trim()) || "";
    var floorplanTitle = decodeHTMLEntity(floorplan.title);
    var hasAvailability = floorplan.unitsAvailable > 0 &&
        (detailUrl.length > 0 || floorplan.availabilityURL.length > 0);
    var availabilityHref = detailUrl || floorplan.availabilityURL;
    var isExternalAvailabilityLink = hasAvailability && detailUrl.length === 0;
    return (React.createElement(Card, { className: "w-full max-w-sm overflow-hidden flex flex-col" },
        React.createElement(CardHeader, { className: "p-0" },
            React.createElement(FloorplanImageDialog, { floorplan: floorplan })),
        React.createElement(CardContent, { className: "p-6 pt-2 space-y-6 flex flex-col justify-center" },
            React.createElement("div", { className: "space-y-4" },
                React.createElement("div", { className: "flex gap-3 flex-wrap" },
                    floorplan.floorplanType && (React.createElement(Badge, { variant: "outline" }, floorplan.floorplanType.charAt(0).toUpperCase() + floorplan.floorplanType.slice(1))),
                    floorplan.unitsAvailable > 0 && (React.createElement(Badge, { variant: "outline" },
                        floorplan.unitsAvailable,
                        " Units Available")),
                    floorplan.floorplanHasSpecials && (React.createElement(Badge, { variant: "outline" }, "Special Promotion"))),
                React.createElement("h3", { className: "text-2xl mt-0 pt-0 font-semibold font-sans" }, detailUrl ? (React.createElement("a", { href: detailUrl, className: "hover:underline" }, floorplanTitle)) : (floorplanTitle)),
                !price.includes("-1") && (React.createElement("div", { className: "flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary/5 w-fit" },
                    React.createElement("span", { className: "text-xs text-foreground/70" }, "Starting at"),
                    React.createElement("span", { className: "text-base font-bold text-primary" }, Number(parseInt(price.replace(/[$,]/g, ""))).toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    })))),
                React.createElement("div", { className: "flex flex-wrap gap-3 text-xs" },
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(BedDouble, { className: "h-4 w-4" }),
                        React.createElement("span", null,
                            floorplan.bedroomCount,
                            " bed")),
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Bath, { className: "h-4 w-4" }),
                        React.createElement("span", null,
                            floorplan.bathroomCount,
                            " bath")),
                    React.createElement("div", { className: "flex items-center gap-2" },
                        React.createElement(Ruler, { className: "h-4 w-4" }),
                        React.createElement("span", null, floorplan.minimumSQFT === floorplan.maximumSQFT
                            ? "".concat(floorplan.minimumSQFT, "sqft")
                            : "".concat(floorplan.minimumSQFT, "-").concat(floorplan.maximumSQFT, "sqft")))))),
        React.createElement(CardFooter, { className: "flex flex-col gap-2 mt-auto" },
            canShowRentCafeFeesEmbed(costEstimatorPropertyId, floorplan) ? (React.createElement(FloorplanFeesDialog, { floorplan: floorplan, costEstimatorPropertyId: costEstimatorPropertyId })) : null,
            floorplan.virtualTourEmbedCode && (React.createElement(FloorplanVirtualTourDialog, { floorplan: floorplan })),
            floorplan.propertyVideoEmbedCode && (React.createElement(FloorplanPropertyVideoDialog, { floorplan: floorplan })),
            hasAvailability ? (React.createElement(Button, { asChild: true, variant: "default", className: "w-full mt-auto" },
                React.createElement("a", __assign({ href: availabilityHref, "aria-label": isExternalAvailabilityLink
                        ? "View availability for ".concat(floorplanTitle, ". Opens in a new browser tab")
                        : "View availability for ".concat(floorplanTitle) }, (isExternalAvailabilityLink
                    ? { target: "_blank", rel: "noreferrer" }
                    : {})), "View Availability"))) : (React.createElement(Button, { asChild: true, variant: "secondary", className: "w-full mt-auto" },
                React.createElement("a", { href: "/contact", "aria-label": "Contact us about ".concat(floorplanTitle) }, "Contact Us"))))));
}
